import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { Box, Button, Card, CardContent, Chip, Divider, Grid, IconButton, Paper, Tooltip, Typography, } from "@mui/material";
import { formatDistanceToNow } from "date-fns";
import React from "react";
import { Link } from "react-router-dom";
import { useCancelInvitation, useGenerateInviteLink, useListTeamInvitations, useRetrieveTeamInviteLink, useRevokeInviteLink, } from "../api/InvitationApiProvider";
import { useRetrieveTeam, useRetrieveUserTeamPermissions, } from "../ApiProvider";
import { ROUTES } from "../breadcrumbs/routeConfig";
import { EmptyState } from "../common/EmptyState";
import { ErrorView } from "../nav/Error";
import { Forbidden } from "../nav/Forbidden";
import { Loading } from "../nav/Loading";
import { DeleteDialog } from "../utils/DeleteDialog";
import ErrorSnackbar from "../utils/ErrorSnackbar";
import SuccessSnackbar from "../utils/SuccessSnackbar";
import { useNumericParams } from "../utils/useNumericParams";
export const TeamInvitations = () => {
    const { id: teamId } = useNumericParams();
    const { data: permissions, isLoading: isPermLoading } = useRetrieveUserTeamPermissions({ teamId });
    const canModify = permissions?.canModify === true;
    const { data: team, isLoading: isTeamLoading, error: teamError, } = useRetrieveTeam({ id: teamId, enabled: canModify });
    const { data: invitations, isLoading: isInvLoading, error: invError, } = useListTeamInvitations({ teamId, enabled: canModify });
    const [successMessage, setSuccessMessage] = React.useState("");
    const [errorMessage, setErrorMessage] = React.useState("");
    if (isPermLoading || (canModify && (isTeamLoading || isInvLoading))) {
        return _jsx(Loading, {});
    }
    if (!canModify) {
        return _jsx(Forbidden, {});
    }
    if (teamError || invError || !team) {
        return _jsx(ErrorView, { error: teamError || invError });
    }
    return (_jsxs(React.Fragment, { children: [_jsxs(Grid, { container: true, spacing: 2, alignItems: "center", children: [_jsx(Grid, { item: true, xs: true, children: _jsxs(Typography, { variant: "h4", gutterBottom: true, children: ["Invitations \u2014 ", team.name] }) }), _jsx(Grid, { item: true, children: _jsx(Link, { to: ROUTES.TEAM_INVITATIONS_SEND.replace(":id", String(teamId)), style: { textDecoration: "none" }, children: _jsx(Button, { variant: "contained", color: "primary", startIcon: _jsx(PersonAddIcon, {}), children: "Send Invitation" }) }) })] }), _jsx(Divider, { sx: { my: 2 } }), _jsx(InviteLinkSection, { teamId: teamId, onSuccess: setSuccessMessage, onError: setErrorMessage }), _jsx(Typography, { variant: "h6", sx: { mt: 4, mb: 2 }, children: "Pending Invitations" }), invitations && invitations.length > 0 ? (_jsx(Grid, { container: true, spacing: 2, children: invitations.map((inv) => (_jsx(Grid, { item: true, xs: 12, sm: 6, md: 4, children: _jsx(PendingInvitationCard, { invitation: inv, teamId: teamId, onSuccess: setSuccessMessage, onError: setErrorMessage }) }, inv.id))) })) : (_jsx(EmptyState, { icon: _jsx(MailOutlineIcon, {}), heading: "No pending invitations", description: "Send an invitation to a player to get started." })), _jsx(SuccessSnackbar, { open: !!successMessage, onClose: () => setSuccessMessage(""), message: successMessage }), _jsx(ErrorSnackbar, { open: !!errorMessage, onClose: () => setErrorMessage(""), message: errorMessage })] }));
};
const InviteLinkSection = ({ teamId, onSuccess, onError, }) => {
    const { data: existingLink } = useRetrieveTeamInviteLink({ teamId });
    const [generatedLink, setGeneratedLink] = React.useState(null);
    const [copied, setCopied] = React.useState(false);
    const linkData = generatedLink ?? existingLink ?? null;
    const generateMutation = useGenerateInviteLink({
        teamId,
        onSuccess: (response) => {
            setGeneratedLink(response.data);
            onSuccess("Invite link generated!");
        },
        onError: () => onError("Failed to generate invite link."),
    });
    const revokeMutation = useRevokeInviteLink({
        teamId,
        onSuccess: () => {
            setGeneratedLink(null);
            onSuccess("Invite link revoked.");
        },
        onError: () => onError("Failed to revoke invite link."),
    });
    const joinUrl = linkData
        ? `${window.location.origin}/join/team/${linkData.token}`
        : "";
    const handleCopy = () => {
        navigator.clipboard.writeText(joinUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };
    return (_jsxs(Paper, { variant: "outlined", sx: { p: 2 }, children: [_jsxs(Box, { sx: {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 1,
                }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [_jsx(LinkIcon, { color: "primary" }), _jsx(Typography, { variant: "h6", children: "Invite Link" })] }), !linkData ? (_jsx(Button, { variant: "outlined", startIcon: _jsx(LinkIcon, {}), disabled: generateMutation.isLoading, onClick: () => generateMutation.mutate(), children: generateMutation.isLoading ? "Generating..." : "Generate Link" })) : (_jsx(Button, { variant: "outlined", color: "error", startIcon: _jsx(LinkOffIcon, {}), disabled: revokeMutation.isLoading, onClick: () => revokeMutation.mutate(), children: revokeMutation.isLoading ? "Revoking..." : "Revoke Link" }))] }), linkData && (_jsxs(Box, { sx: {
                    mt: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    bgcolor: "grey.50",
                    p: 1.5,
                    borderRadius: 1,
                }, children: [_jsx(Typography, { variant: "body2", sx: {
                            flexGrow: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }, children: joinUrl }), _jsx(Tooltip, { title: copied ? "Copied!" : "Copy link", children: _jsx(IconButton, { size: "small", onClick: handleCopy, children: _jsx(ContentCopyIcon, { fontSize: "small" }) }) })] })), _jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mt: 1 }, children: "Anyone with the link can join this team. Links expire after 30 days." })] }));
};
const PendingInvitationCard = ({ invitation, teamId, onSuccess, onError, }) => {
    const [showCancel, setShowCancel] = React.useState(false);
    const cancelMutation = useCancelInvitation({
        onMutate: () => { },
        onSuccess: () => {
            setShowCancel(false);
            onSuccess("Invitation cancelled.");
        },
        onError: () => {
            setShowCancel(false);
            onError("Failed to cancel invitation.");
        },
    });
    const inviteeName = invitation.invitee
        ? `${invitation.inviteeFirstName} ${invitation.inviteeLastName}`
        : invitation.email || "Unknown";
    return (_jsxs(_Fragment, { children: [_jsx(Card, { variant: "outlined", children: _jsxs(CardContent, { children: [_jsxs(Box, { sx: {
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                            }, children: [_jsxs(Box, { children: [_jsx(Typography, { variant: "subtitle1", fontWeight: "bold", children: inviteeName }), invitation.email && !invitation.invitee && (_jsx(Typography, { variant: "body2", color: "text.secondary", children: invitation.email }))] }), _jsx(Tooltip, { title: "Cancel invitation", children: _jsx(IconButton, { size: "small", color: "error", onClick: () => setShowCancel(true), children: _jsx(DeleteIcon, { fontSize: "small" }) }) })] }), invitation.mergePlayer && (_jsx(Chip, { label: "Player merge", size: "small", color: "info", sx: { mt: 1 } })), _jsxs(Typography, { variant: "caption", color: "text.secondary", sx: { display: "block", mt: 1 }, children: ["Sent", " ", formatDistanceToNow(new Date(invitation.createdAt), {
                                    addSuffix: true,
                                })] })] }) }), _jsx(DeleteDialog, { open: showCancel, title: "Cancel Invitation", description: `Are you sure you want to cancel the invitation to ${inviteeName}?`, onCancel: () => setShowCancel(false), onConfirm: () => cancelMutation.mutate({ invitationId: invitation.id, teamId }), confirmText: "Cancel Invitation", confirmColor: "error" })] }));
};
